"use client"

import { ProductType } from "@/constants/booking/order"
import TripItem from "@/components/profile/MyBooking/TripItem"
import { bookingActions } from "@/lib/redux/features/booking/reduce"
import { PendingOrder } from "@/lib/redux/features/booking/type"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { parseJson } from "@/utils/safeParse/parseJson"
import { List } from "antd"
import _ from "lodash"
import { FC } from "react"
import { useTranslation } from "react-i18next"

type ListPendingOrdersProps = {
  showEmpty?: boolean
}

const ListPendingOrders: FC<ListPendingOrdersProps> = ({ showEmpty = true }) => {
  const pendingOrders = useAppSelector(state => state.booking.pendingOrders)
  const dispatch = useAppDispatch()

  const handleDelete = ({ order: currentOrder }: PendingOrder) => {
    const cachedPendingOrders = parseJson(localStorage.getItem('orders')) || []
    dispatch(bookingActions.updatePendingOrders(cachedPendingOrders.filter(({ order }: PendingOrder) => currentOrder.orderCode !== order.orderCode)))
  }
  const { t } = useTranslation()

  if (!showEmpty && pendingOrders.length <= 0) {
    return <></>
  }

  return (
    <List
      dataSource={pendingOrders}
      renderItem={({ order: item, ctime, ...other }: any) => {
        // Handle different product types differently
        if (item.productType === ProductType.HOTEL) {
          // Check if this is the custom hotelInfo object from index.tsx
          if (item.hotelName !== undefined) {
            // This is the custom hotelInfo object
            return (
              <List.Item>
                <TripItem
                  bookingId={item.bookingId}
                  status={item.bookingStatus || "COMPLETED"}
                  productType={ProductType.HOTEL}
                  hotelName={item.hotelName || "Hotel"}
                  address={item.address || ""}
                  checkIn={item.checkIn}
                  checkOut={item.checkOut}
                  roomType={item.bookingDetails?.property?.rooms ?
                    item.bookingDetails.property.rooms[0]?.roomType || "" :
                    ""}
                  guestCount={`${item.guestAmount || 1} ${_.lowerFirst(t("common.passenger"))}`}
                  rating={4}
                  totalAmount={item.totalAmount}
                  orderData={item}
                  timmer={ctime}
                  showCheckoutNoti
                  shownDelete
                  onClick={() => { }}
                  onDelete={() => handleDelete({ ...other, order: item, ctime })}
                />
              </List.Item>
            );
          } else {
            // This is the original order data format
            const hotelInfo =
              typeof item.itinData === "string"
                ? parseJson(item.itinData) || {}
                : item.itinData;

            const propertyName = hotelInfo?.property?.propertyName;
            const address = [
              hotelInfo?.property?.addressLine1,
              hotelInfo?.property?.addressLine2,
              hotelInfo?.property?.city,
              hotelInfo?.property?.country,
            ]
              .filter(Boolean)
              .join(", ");

            const roomType = hotelInfo?.room?.roomType;
            const guestCount = hotelInfo?.occupancy
              ? `${hotelInfo.occupancy.numberOfAdults} Adults${hotelInfo.occupancy.numberOfChildren
                ? `, ${hotelInfo.occupancy.numberOfChildren} Children`
                : ""
              }`
              : "";

            return (
              <List.Item>
                <TripItem
                  bookingId={item.orderCode}
                  status={item.bookingStatus}
                  productType={ProductType.HOTEL}
                  hotelName={propertyName || "Hotel"}
                  address={address || ""}
                  checkIn={hotelInfo?.checkIn}
                  checkOut={hotelInfo?.checkOut}
                  roomType={roomType || ""}
                  guestCount={guestCount}
                  rating={4}
                  totalAmount={item.totalAmount}
                  orderData={item}
                  timmer={ctime}
                  showCheckoutNoti
                  shownDelete
                  onClick={() => { }}
                  onDelete={() => handleDelete({ ...other, order: item, ctime })}
                />
              </List.Item>
            );
          }
        } else {
          // Default case for flight bookings
          const firstItin =
            item.itinData && item.itinData.length > 0
              ? typeof item.itinData === "string"
                ? (parseJson(item.itinData) || [])[0]
                : item.itinData[0]
              : {};

          return (
            <List.Item>
              <TripItem
                bookingId={item.orderCode}
                status={item.bookingStatus}
                productType={item.productType}
                from={firstItin?.startPoint || ""}
                to={firstItin?.endPoint || ""}
                startTime={firstItin?.startTime}
                endTime={firstItin?.endTime}
                airline={firstItin?.airline}
                flightNumber={firstItin?.flightNumber}
                totalAmount={item.totalAmount}
                orderData={item}
                timmer={ctime}
                showCheckoutNoti
                shownDelete
                onClick={() => { }}
                onDelete={() => handleDelete({ ...other, order: item, ctime })}
              />
            </List.Item>
          );
        }
      }}
    />
  )
}

export default ListPendingOrders